import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Bed, Bath, MapPin, Maximize } from 'lucide-react';
import { motion } from 'framer-motion';
import { toggleSavedProperty, getSavedProperties } from '@/lib/api';
import { toast } from 'sonner';
import { buildDisplayName } from '@/lib/propertyUtils';
import { useCurrency } from '@/contexts/CurrencyContext';

const PropertyCard = ({ property, index = 0, priceType = null }) => {
  const displayName = buildDisplayName(property);
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { formatPropertyPrice } = useCurrency();

  useEffect(() => {
    const checkSaved = async () => {
      try {
        const res = await getSavedProperties();
        setIsSaved(res.data.saved_ids?.includes(property.id));
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Saved-state check failed:', e);
      }
    };
    checkSaved();
  }, [property.id]);

  const handleSaveToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      const res = await toggleSavedProperty(property.id);
      setIsSaved(res.data.saved);
      toast.success(res.data.saved ? 'Property saved!' : 'Property removed from saved');
      window.dispatchEvent(new Event('savedPropertyChange'));
    } catch (err) {
      toast.error('Failed to save property');
    } finally {
      setIsLoading(false);
    }
  };

  const statusLabel = {
    for_sale: 'For Sale',
    for_rent: 'For Rent',
    for_rent_short: 'Managed by Lalita',
    for_rent_long: 'Long-term Rental',
    managed: 'Managed',
  };

  const statusColor = {
    for_sale: 'bg-ubud-jungle',
    for_rent: 'bg-temple-gold',
    for_rent_short: 'bg-temple-gold',
    for_rent_long: 'bg-amber-700',
    managed: 'bg-charcoal-light',
  };

  const ownershipLabel = {
    leasehold: 'Leasehold',
    freehold: 'Freehold',
    both: 'Freehold & Leasehold',
    yearly_rental: 'Yearly Rental',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="h-full"
      data-testid={`property-card-${property.id}`}
    >
      <Link
        to={`/properties/${property.slug || property.id}`}
        className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-floating transition-all duration-500 border border-transparent hover:border-warm-sand"
      >
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden flex-shrink-0">
          <img
            src={property.cover_image || property.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'}
            alt={displayName}
            className="w-full h-full object-cover property-card-image"
            loading="lazy"
          />
          
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
          
          {/* Badges Container */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {/* Status Badge */}
            <div className={`${statusColor[property.status]} text-white text-xs font-medium px-2.5 py-1 rounded-full`}>
              {statusLabel[property.status]}
            </div>
            
            {/* Ownership Type Badge - Only show for for_sale properties */}
            {property.status === 'for_sale' && property.ownership_type && (
              <div className="bg-white/90 text-charcoal text-xs font-medium px-2.5 py-1 rounded-full">
                {ownershipLabel[property.ownership_type]}
                {/* Surface leasehold term inline so buyers see it without opening the listing */}
                {(property.ownership_type === 'leasehold' || property.ownership_type === 'both') && (property.lease_years_remaining || property.lease_expiry_year) && (
                  <span className="ml-1 text-charcoal-muted">
                    · {property.lease_years_remaining ? `${property.lease_years_remaining}y left` : `until ${property.lease_expiry_year}`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveToggle}
            disabled={isLoading}
            className={`absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white transition-all duration-200 heart-btn ${isSaved ? 'active' : ''}`}
            data-testid={`save-property-${property.id}`}
          >
            <Heart
              className={`w-4 h-4 transition-colors ${isSaved ? 'fill-red-600 text-red-600' : 'text-charcoal'}`}
            />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-4">
          {/* Location */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1 text-charcoal-muted">
              <MapPin className="w-3.5 h-3.5" />
              <span className="text-xs font-body">{property.location}</span>
            </div>
            {property.listing_code && (
              <span className="font-mono text-[10px] bg-warm-sand text-charcoal-muted px-1.5 py-0.5 rounded">
                {property.listing_code}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-heading text-base font-medium text-charcoal mb-1 group-hover:text-ubud-jungle transition-colors line-clamp-1">
            {displayName}
          </h3>

          {/* Details */}
          <div className="flex items-center gap-3 text-charcoal-light text-xs mt-auto pt-2">
            {property.property_type === 'land' ? (
              property.land_size ? (
                <div className="flex items-center gap-1">
                  <Maximize className="w-3.5 h-3.5" />
                  <span>{property.land_size} m²</span>
                </div>
              ) : (
                <span className="capitalize">Land</span>
              )
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <Bed className="w-3.5 h-3.5" />
                  <span>{property.bedrooms} Beds</span>
                </div>
                <div className="flex items-center gap-1">
                  <Bath className="w-3.5 h-3.5" />
                  <span>{property.bathrooms} Baths</span>
                </div>
              </>
            )}
          </div>

          {/* Price */}
          {(() => {
            // Determine effective price type & unit label for the card.
            // Priority: explicit `priceType` prop → infer from status + populated price fields.
            let effectiveType = priceType;
            if (!effectiveType) {
              if (property.status === 'for_rent_short') {
                effectiveType = 'nightly';
              } else if (property.status === 'for_rent_long' || property.status === 'for_rent') {
                if (property.monthly_rental_price) effectiveType = 'monthly';
                else if (property.yearly_rental_price) effectiveType = 'yearly';
                else effectiveType = 'long-term'; // generic long-term, no specific period
              } else if (property.status === 'for_sale' && property.ownership_type === 'yearly_rental') {
                effectiveType = 'yearly';
              }
            }

            let priceValue = null;
            let idrAnchor = null;
            let prefix = '';
            if (effectiveType === 'monthly') {
              priceValue = property.monthly_rental_price;
              idrAnchor = property.monthly_rental_price_idr;
            } else if (effectiveType === 'yearly') {
              priceValue = property.yearly_rental_price || (property.status === 'for_sale' ? property.price : null);
              idrAnchor = property.yearly_rental_price_idr || (property.status === 'for_sale' ? property.price_idr : null);
            } else if (effectiveType === 'nightly') {
              priceValue = property.rental_price;
              idrAnchor = property.rental_price_idr;
              prefix = 'From ';
            } else if (effectiveType === 'long-term') {
              priceValue = property.rental_price || property.price;
              idrAnchor = property.rental_price_idr || property.price_idr;
              prefix = 'From ';
            } else {
              priceValue = property.price;
              idrAnchor = property.price_idr;
            }

            const unitLabel = {
              monthly: '/ month',
              yearly: '/ year',
              nightly: '/ night',
            }[effectiveType];

            const hasPrice = !!(priceValue || idrAnchor);

            // Show BOTH monthly + yearly on cards when both exist and no
            // specific priceType is forced — fixes the "only monthly shown"
            // bug flagged in the PDF revision.
            const showBothLongTerm =
              !priceType &&
              (property.status === 'for_rent_long' || property.status === 'for_rent') &&
              (property.monthly_rental_price || property.monthly_rental_price_idr) &&
              (property.yearly_rental_price || property.yearly_rental_price_idr);

            if (showBothLongTerm) {
              return (
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5" data-testid="rental-both-prices">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-heading text-lg font-semibold text-charcoal">
                      {formatPropertyPrice(property.monthly_rental_price, property.monthly_rental_price_idr)}
                    </span>
                    <span className="text-charcoal-muted text-xs">/ month</span>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-heading text-sm font-medium text-charcoal-light">
                      {formatPropertyPrice(property.yearly_rental_price, property.yearly_rental_price_idr)}
                    </span>
                    <span className="text-charcoal-muted text-xs">/ year</span>
                  </div>
                </div>
              );
            }

            return (
              <div className="flex items-baseline gap-1.5 mt-2 pt-2 border-t border-gray-100">
                <span className="font-heading text-lg font-semibold text-charcoal">
                  {hasPrice ? `${prefix}${formatPropertyPrice(priceValue, idrAnchor)}` : 'Price on Request'}
                </span>
                {hasPrice && unitLabel && (
                  <span className="text-charcoal-muted text-xs">{unitLabel}</span>
                )}
              </div>
            );
          })()}
          {property.property_type === 'land' && property.price > 0 && property.land_size > 0 && (
            <div className="text-charcoal-muted text-xs mt-1" data-testid="land-price-per-are">
              {formatPropertyPrice((property.price / property.land_size) * 100)} / 100 m² (1 are)
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
